import { ANY_ATTACHED, type AttachedChoices, type AttachedFilter, type AttachedWithFilter } from "../../../api/content/searchApi";
import { iriLocalName } from "../../../shared/references/shortReferences";

/**
 * The attached-data filter as the controls change it (F88, F91). A kind, a label and a term
 * all narrow it; "nothing attached" stands alone, as the server takes it.
 */

/** One label or model term the chooser offers, by the name a historian reads. */
export interface AttachedFacet {
  readonly kind: "label" | "term";
  /** As the filter names it: the label's `<list>#<key>`, or the term's IRI. */
  readonly value: string;
  readonly name: string;
  readonly documents: number;
  /** A label's documents per kind of segment it lies in (F141); empty for a term, which lies in none. */
  readonly segments: Readonly<Record<string, number>>;
}

/**
 * The facets a search limited to `segmentType` can name (F141; Simeon's header and subentry
 * labels): the labels that lie in it, each with its documents there, and every term, since
 * terms count over the whole document. Without a kind of segment, all of them.
 */
export function facetsIn(facets: readonly AttachedFacet[], segmentType: string | null): readonly AttachedFacet[] {
  if (segmentType === null) return facets;
  return facets.flatMap((facet): AttachedFacet[] => {
    if (facet.kind === "term") return [facet];
    const documents = facet.segments[segmentType] ?? 0;
    return documents === 0 ? [] : [{ ...facet, documents }];
  });
}

/** The kind a filter keeps: one data class, everything, or nothing attached. */
export type KindChoice = { readonly kind: "any" } | { readonly kind: "nothing" } | { readonly kind: "class"; readonly iri: string };

export function kindChoiceOf(filter: AttachedFilter): KindChoice {
  if (filter.kind === "nothing") return { kind: "nothing" };
  const [iri] = filter.kinds;
  return iri === undefined ? { kind: "any" } : { kind: "class", iri };
}

export function withKindChoice(filter: AttachedFilter, choice: KindChoice): AttachedFilter {
  const kept: AttachedWithFilter = filter.kind === "with" ? filter : { ...ANY_ATTACHED, acceptedOnly: filter.acceptedOnly };
  switch (choice.kind) {
    case "nothing":
      return { kind: "nothing", acceptedOnly: filter.acceptedOnly };
    case "any":
      return { ...kept, kinds: [] };
    case "class":
      return { ...kept, kinds: [choice.iri] };
  }
}

export function withAcceptedOnly(filter: AttachedFilter, acceptedOnly: boolean): AttachedFilter {
  return { ...filter, acceptedOnly };
}

/** The filter with a label or term added; nothing-attached becomes "attached with it". */
export function withFacet(filter: AttachedFilter, facet: AttachedFacet): AttachedFilter {
  const kept: AttachedWithFilter = filter.kind === "with" ? filter : { ...ANY_ATTACHED, acceptedOnly: filter.acceptedOnly };
  return facet.kind === "label"
    ? { ...kept, labels: [...new Set([...kept.labels, facet.value])] }
    : { ...kept, terms: [...new Set([...kept.terms, facet.value])] };
}

export function withoutFacet(filter: AttachedFilter, facet: Pick<AttachedFacet, "kind" | "value">): AttachedFilter {
  if (filter.kind === "nothing") return filter;
  return facet.kind === "label"
    ? { ...filter, labels: filter.labels.filter((label) => label !== facet.value) }
    : { ...filter, terms: filter.terms.filter((term) => term !== facet.value) };
}

/** How many choices narrow the filter; 0 keeps every document. */
export function narrowingCount(filter: AttachedFilter): number {
  const accepted = filter.acceptedOnly ? 1 : 0;
  return filter.kind === "nothing" ? 1 + accepted : filter.kinds.length + filter.labels.length + filter.terms.length + accepted;
}

/**
 * Every label and term the project's attached data uses, labels first, each by its name: a label
 * by its label list, a term by `termName`, the one term-name owner's (PC-5).
 */
export function facetsOf(choices: AttachedChoices, labelNames: ReadonlyMap<string, string>, termName: (iri: string) => string): readonly AttachedFacet[] {
  return [
    ...choices.labels.map((label): AttachedFacet => ({ kind: "label", value: label.label, name: labelNames.get(label.label) ?? label.key, documents: label.documents, segments: label.segments })),
    ...choices.terms.map((term): AttachedFacet => ({ kind: "term", value: term.term, name: termName(term.term), documents: term.documents, segments: {} })),
  ];
}

/** The facets the filter doesn't name yet whose name holds `text` (case-folded); a term's local name finds it too. */
export function findFacets(facets: readonly AttachedFacet[], filter: AttachedFilter, text: string): readonly AttachedFacet[] {
  const needle = text.trim().toLocaleLowerCase();
  const chosen = new Set(filter.kind === "with" ? [...filter.labels, ...filter.terms] : []);
  const holds = (name: string) => name.toLocaleLowerCase().includes(needle);
  return facets.filter((facet) => !chosen.has(facet.value) && (holds(facet.name) || (facet.kind === "term" && holds(iriLocalName(facet.value)))));
}
